import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Drawer } from '@material-ui/core';
import { Row, Col } from 'reactstrap';
import Dropzone from 'react-dropzone-uploader';
import 'react-dropzone-uploader/dist/styles.css';
import Toaster from '../common/Toaster';
import DayTime from '../common/DayTimeWrapper';
import LoaderCustom from '../common/LoaderCustom';
import { AttachmentValidationInfo } from '../../config/message';
import { loggedInUserId, userDetails } from '../../helper';
import { getChatMessages, sendChatMessage, uploadChatFiles } from './actions/rfq';
import { FILE_URL } from '../../config/constants';
import redcrossImg from '../../assests/images/red-cross.png';

/**
 * ChatDrawer Component
 * Displays vendor chat messages and allows user to send replies
 *
 * Props:
 * - isOpen: boolean - Controls drawer visibility
 * - closeDrawer: function - Callback to close drawer
 * - rowData: object - Contains QuotationVendorId, QuotationPartId, QuotationPlantId, QuotationCostingId
 * - quotationNumber: string - Display quotation number in header
 * - onMessagesRead: function - Callback to refresh parent data when messages are read
 */
function ChatDrawer(props) {
    const { isOpen, closeDrawer, rowData, quotationNumber, onMessagesRead } = props;
    const dispatch = useDispatch();

    const [loader, setLoader] = useState(false);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatMasterId, setChatMasterId] = useState(null);
    const [replyMessage, setReplyMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [attachmentAccordion, setAttachmentAccordion] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [hasReadMessages, setHasReadMessages] = useState(false); // Track if messages were read
    const chatEndRef = useRef(null);
    const dropzoneRef = useRef(null);

    // Auto-scroll to bottom when new messages arrive
    const scrollToBottom = () => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [chatMessages]);

    // Fetch chat messages when drawer opens
    useEffect(() => {
        if (isOpen && rowData) {
            fetchChatMessages();
        }
    }, [isOpen, rowData]);

    /**
     * Fetch chat messages from API
     * API Endpoint: POST /quotation-chat/get-messages
     */
    const fetchChatMessages = () => {
        if (!rowData) {
            Toaster.error('No chats are available');
            return;
        }

        setLoader(true);

        const payload = {
            QuotationVendorId: rowData?.QuotationVendorId || rowData?.VendorId || null,
            QuotationPartId: rowData?.QuotationPartId || rowData?.PartId || null,
            QuotationPlantId: rowData?.QuotationPlantId || rowData?.PlantId || null,
            QuotationCostingId: rowData?.QuotationCostingId || rowData?.CostingId || null,
            LoggedInUserId: loggedInUserId(),
            IsReaderFromBase: "true"
        };

        dispatch(getChatMessages(payload, (response) => {
            setLoader(false);
            if (response?.status === 200 && response?.data?.Result && response?.data?.Data?.Messages) {
                setChatMessages(response.data.Data.Messages);
                // Store the QuotationChatMasterId for sending messages
                if (response.data.Data.ChatDetails?.QuotationChatMasterId) {
                    setChatMasterId(response.data.Data.ChatDetails.QuotationChatMasterId);
                }
                // Mark that messages have been read (for refreshing unread count on close)
                setHasReadMessages(true);
            } else if (response?.status === 204) {
                setChatMessages([]);
                setChatMasterId(null);
            } else {
                setChatMessages([]);
                setChatMasterId(null);
                if (response?.data?.Message) {
                    Toaster.error(response.data.Message);
                }
            }
        }));
    };

    /**
     * Send reply message
     * API Endpoint: POST /quotation-chat/send-message
     */
    const handleSendReply = () => {
        if (!replyMessage.trim() && attachments.length === 0) {
            Toaster.warning('Please enter a message or attach files');
            return;
        }

        if (!chatMasterId) {
            Toaster.error('Chat session not found. Please refresh and try again.');
            return;
        }

        setSending(true);

        // Map attachments to the required format
        const formattedAttachments = attachments.map(att => {
            // Extract file extension from originalFileName
            const fileExtension = att.originalFileName?.split('.').pop() || '';

            return {
                FileName: att.attachmentId,
                FileURL: att.fileUrl,
                OriginalFileName: att.originalFileName,
                FileSize: att.fileSize,
                MimeType: att.mimeType,
                FileExtension: fileExtension
            };
        });

        const payload = {
            QuotationChatMasterId: chatMasterId,
            MessageText: replyMessage.trim(),
            IsSenderFromBase: true,
            LoggedInUserId: loggedInUserId(),
            Attachments: formattedAttachments
        };

        dispatch(sendChatMessage(payload, (response) => {
            setSending(false);
            if (response?.status === 200 && response?.data?.Result) {
                Toaster.success('Message sent successfully');
                setReplyMessage('');
                setAttachments([]); // Clear attachments after successful send
                setAttachmentAccordion(false); // Close attachment accordion
                // Clear dropzone files
                if (dropzoneRef.current) {
                    dropzoneRef.current.files = [];
                }
                fetchChatMessages(); // Refresh messages to show the new message
            } else {
                const errorMessage = response?.data?.Message || 'Failed to send message';
                Toaster.error(errorMessage);
            }
        }));
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendReply();
        }
    };

    /**
     * Handle drawer close
     * Refreshes parent data to update unread count if messages were read
     */
    const handleDrawerClose = () => {
        if (hasReadMessages && onMessagesRead) {
            onMessagesRead(); // Refresh parent data to update unread count
        }
        setHasReadMessages(false); // Reset for next open
        closeDrawer();
    };

    /**
     * Handle file upload status change
     * Uploads file to /quotation-chat/file-upload API
     */
    const handleChangeStatus = ({ meta, file }, status) => {
        if (status === 'done') {
          setUploading(true);
          const formData = new FormData();
          formData.append('file', file);
        //   formData.append('quotationPartId', data.QuotationPartId);
          dispatch(
            uploadChatFiles(formData, (res) => {
              setUploading(false);
              // Handle new API response format: array with FileName, FileURL, OriginalFileName, FileSize, MimeType, Exist
              if (res?.data && Array.isArray(res.data) && res.data.length > 0) {
                const uploadedFile = res.data[0];
                // Map the response to the format used in the component
                const mappedAttachment = {
                  attachmentId: uploadedFile.FileName, // Use FileName as unique identifier
                  fileUrl: uploadedFile.FileURL,
                  originalFileName: uploadedFile.OriginalFileName,
                  fileSize: uploadedFile.FileSize,
                  mimeType: uploadedFile.MimeType
                };
                setAttachments((prev) => [...prev, mappedAttachment]);
                Toaster.success('File uploaded successfully');
              } else {
                Toaster.error('Error uploading file');
                // Remove from dropzone
                if (dropzoneRef.current) {
                  dropzoneRef.current.files = dropzoneRef.current.files.filter(
                    f => f.meta.id !== meta.id
                  );
                }
              }
            })
          );
        } else if (status === 'removed') {
          // Handle file removal from dropzone
          setAttachments((prev) => prev.filter((att) => att.originalFileName !== meta.name));
        }
      };

    /**
     * Delete uploaded file
     */
    const deleteFile = (attachmentId, fileName) => {
        setAttachments(prev => prev.filter(a => a.attachmentId !== attachmentId));
        Toaster.success(`${fileName} removed successfully`);
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={handleDrawerClose}
            className="chat-drawer-container"
        >
            <div className="drawer-wrapper chat-drawer-wrapper">
                {loader && <LoaderCustom customClass="loader-center" />}

                {/* Header */}
                <div className="drawer-header">
                    <Row className="no-gutters align-items-center">
                        <Col md="10">
                            <h4 className="drawer-title">
                                Vendor Support 
                            </h4>
                        </Col>
                        <Col md="2" className="text-right">
                            <button
                                type="button"
                                className="close-drawer"
                                onClick={handleDrawerClose}
                                title="Close"
                            >
                                ×
                            </button>
                        </Col>
                    </Row>
                </div>

                {/* Chat Messages Container */}
                <div className="drawer-body chat-messages-container">
                    {chatMessages.length === 0 ? (
                        <div className="no-messages-placeholder">
                            <p>No messages yet</p>
                        </div>
                    ) : (
                        <div className="messages-list">
                            {chatMessages.map((msg, index) => {
                                // Get current message date
                                const currentMessageDate = DayTime(msg.SentDate).format('DD/MM/YYYY');
                                // Get previous message date (if exists)
                                const previousMessageDate = index > 0 ? DayTime(chatMessages[index - 1].SentDate).format('DD/MM/YYYY') : null;
                                // Show date separator if this is the first message or date changed
                                const showDateSeparator = index === 0 || currentMessageDate !== previousMessageDate;

                                // Get date label (Today, Yesterday, or date)
                                const getDateLabel = (date) => {
                                    const today = DayTime().format('DD/MM/YYYY');
                                    const yesterday = DayTime().subtract(1, 'days').format('DD/MM/YYYY');

                                    if (date === today) return 'Today';
                                    if (date === yesterday) return 'Yesterday';
                                    return date;
                                };

                                return (
                                    <React.Fragment key={msg.QuotationChatMessageId}>
                                        {/* Date Separator */}
                                        {showDateSeparator && (
                                            <div className="date-separator">
                                                <span className="date-label">{getDateLabel(currentMessageDate)}</span>
                                            </div>
                                        )}

                                        {/* Message Item */}
                                        <div
                                            className={`message-item ${
                                                msg.IsSentByMe ? 'message-sent' : 'message-received'
                                            }`}
                                        >
                                            <div className="message-header">
                                                <span className="sender-name">{msg.SenderName}</span>
                                                <span className="message-time">
                                                    {DayTime(msg.SentDate).format('hh:mm A')}
                                                </span>
                                            </div>
                                            <div className="message-content">{msg.MessageText}</div>
                                            {/* Display attachments if any */}
                                            {msg.Attachments && msg.Attachments.length > 0 && (
                                                <div className="message-attachments">
                                                    {msg.Attachments.map((attachment) => {
                                                        // Remove tilde and prepend FILE_URL for proper download path
                                                        const withOutTild = attachment.FileURL?.replace("~", "");
                                                        const fileURL = withOutTild?.startsWith('http') ? withOutTild : `${FILE_URL}${withOutTild}`;

                                                        return (
                                                            <div key={attachment.QuotationChatAttachmentId} className="attachment-item">
                                                                <a
                                                                    href={fileURL}
                                                                    download={attachment.OriginalFileName}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title={attachment.OriginalFileName}
                                                                >
                                                                    📎 {attachment.OriginalFileName} ({attachment.FileSize})
                                                                </a>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </React.Fragment>
                                )
                            })}
                            <div ref={chatEndRef} />
                        </div>
                    )}
                </div>

                {/* Reply Input Area */}
                <div className="drawer-footer chat-reply-container">
                    <Row className="no-gutters">
                        <Col md="12">
                            <div className="reply-input-wrapper-with-attachment">
                                <textarea
                                    className="form-control reply-textarea"
                                    placeholder="Type your message here..."
                                    value={replyMessage}
                                    onChange={(e) => setReplyMessage(e.target.value)}
                                    onKeyPress={handleKeyPress}
                                    rows={3}
                                    disabled={sending}
                                />
                                <button
                                    className="btn-attachment-icon"
                                    type="button"
                                    onClick={() => setAttachmentAccordion(!attachmentAccordion)}
                                    title="Add Attachment"
                                >
                                    {attachmentAccordion ? (
                                        <i className="fa fa-minus"></i>
                                    ) : (
                                        <i className="fa fa-paperclip"></i>
                                    )}
                                </button>
                            </div>
                        </Col>

                        {/* Attachment Section */}
                        {attachmentAccordion && (
                            <Col md="12" className="mt-2">
                                <Row className="mx-0 border">
                                    <Col md="12" className="pt-3 pb-3">
                                        <label>Upload Attachment (upload up to 4 files, size of each file upto 20MB) <AttachmentValidationInfo /></label>
                                        <div className={`alert alert-danger mt-2 ${attachments.length === 4 ? '' : 'd-none'}`} role="alert">
                                            Maximum file upload limit has been reached.
                                        </div>
                                        <div className={`${attachments.length >= 4 ? 'd-none' : ''}`}>
                                            <Dropzone
                                                ref={dropzoneRef}
                                                onChangeStatus={handleChangeStatus}
                                                PreviewComponent={null}
                                                accept="image/jpeg,image/jpg,image/png,image/PNG,.xls,.doc,.pdf,.xlsx,.zip,.docx,.pptx"
                                                initialFiles={[]}
                                                maxFiles={4}
                                                maxSizeBytes={20000000}
                                                inputContent={(files, extra) =>
                                                    extra.reject ? (
                                                        "Image, audio and video files only"
                                                    ) : (
                                                        <div className="text-center">
                                                            <i className="text-primary fa fa-cloud-upload"></i>
                                                            <span className="d-block">
                                                                Drag and Drop or{" "}
                                                                <span className="text-primary">Browse</span>
                                                                <br />
                                                                file to upload
                                                            </span>
                                                        </div>
                                                    )
                                                }
                                                styles={{
                                                    dropzoneReject: {
                                                        borderColor: "red",
                                                        backgroundColor: "#DAA",
                                                    },
                                                    inputLabel: (files, extra) =>
                                                        extra.reject ? { color: "red" } : {},
                                                }}
                                                disabled={sending || uploading}
                                            />
                                        </div>
                                        {/* Attachment List Display */}
                                        {attachments.length > 0 && (
                                            <div className="p-relative mt-3">
                                                <div className="attachment-wrapper">
                                                    {uploading && <LoaderCustom customClass="attachment-loader" />}
                                                    {attachments.map((f) => {
                                                        const withOutTild = f.fileUrl?.replace("~", "");
                                                        const fileURL = withOutTild.startsWith('blob:') ? withOutTild : `${FILE_URL}${withOutTild}`;
                                                        return (
                                                            <div className="attachment images" key={f.attachmentId}>
                                                                <a href={fileURL} target="_blank" rel="noreferrer">
                                                                    {f.originalFileName}
                                                                </a>
                                                                <img
                                                                    alt="Remove"
                                                                    className="float-right"
                                                                    onClick={() => deleteFile(f.attachmentId, f.originalFileName)}
                                                                    src={redcrossImg}
                                                                    style={{ cursor: 'pointer' }}
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </Col>
                                </Row>
                            </Col>
                        )}

                        <Col md="12" className="text-right mt-2">
                            <button
                                type="button"
                                className="btn btn-secondary mr-2"
                                onClick={handleDrawerClose}
                                disabled={sending}
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleSendReply}
                                disabled={sending || (!replyMessage.trim() && attachments.length === 0)}
                            >
                                {sending ? 'Sending...' : 'Send'}
                            </button>
                        </Col>
                    </Row>
                </div>
            </div>
        </Drawer>
    );
}

export default ChatDrawer;